import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../../components/Header/Header";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import {
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_TEL,
} from "../../config/contact";
import "./website-maintenance.css";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const maintenancePlans = [
  {
    title: "Monthly Plan",
    text: "Flexible monthly website maintenance with ongoing updates, monitoring, and support.",
    badge: "Popular",
  },
  {
    title: "Annual Plan",
    text: "Complete year-round website management — content, SEO, security, and performance.",
    badge: "Best Value",
  },
  {
    title: "Prepaid Plan",
    text: "Pay-as-you-go maintenance credits for updates, fixes, and enhancements on demand.",
    badge: "Flexible",
  },
];

const highlights = [
  {
    title: "Increased Website Traffic",
    text: "Proactive SEO and content updates help drive more qualified visitors to your site.",
  },
  {
    title: "Better Website Health",
    text: "Regular audits on Google PageSpeed, AMP, Structured Data, and Lighthouse keep your site performing.",
  },
  {
    title: "Higher Lead Conversion",
    text: "Optimized pages, faster load times, and fresh content improve engagement and conversions.",
  },
  {
    title: "Cost-Effective Packages",
    text: "Affordable prepaid, monthly, and annual plans that save you money in the long run.",
  },
];

const inHouseTeam = [
  "Web Designers",
  "Web Developers",
  "SEO Analysts",
  "Graphic Designers",
  "Content Writers",
  "Website Security Auditors",
];

const auditTools = [
  { name: "Google PageSpeed Insights", logo: "https://cdn.simpleicons.org/google/4285F4" },
  { name: "Google AMP Validation", logo: "https://cdn.simpleicons.org/google/34A853" },
  { name: "Structured Data", logo: "https://cdn.simpleicons.org/googletagmanager/E37400" },
  { name: "Google Lighthouse", logo: "https://cdn.simpleicons.org/lighthouse/F44B21" },
];

const annualServices = [
  "Website content & image updates",
  "Product additions",
  "New page creation",
  "HTML code cleanup & optimization",
  "CSS code cleanup & optimization",
  "JS compression",
  "Competition analysis",
  "Onsite recommendations & implementation",
  "Page speed optimization",
  "Image optimization",
  "Social reputation monitoring",
  "YouTube channel setup & optimization",
  "Social media page designs",
  "SEO consulting",
  "Sitemap updates",
  "Website backup",
  "All browser compatibility",
  "Regular website security audit",
  "Google search ranking maintenance",
];

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const AnimatedSection: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className = "", children }) => (
  <motion.section
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    variants={fadeUp}
    transition={{ duration: 0.6, ease: "easeOut" }}
  >
    {children}
  </motion.section>
);

const SectionHeader: React.FC<{
  eyebrow?: string;
  title: string;
  text?: string;
  light?: boolean;
}> = ({ eyebrow, title, text, light = false }) => (
  <div className={`wmt-section-header ${light ? "wmt-section-header--light" : ""}`}>
    {eyebrow && <span>{eyebrow}</span>}
    <h2>{title}</h2>
    {text && <p>{text}</p>}
  </div>
);

const MaintenanceDashboard = () => (
  <motion.div
    className="wmt-dashboard"
    animate={{ y: [0, -10, 0] }}
    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
  >
    <div className="wmt-dashboard__header">
      <span>Website Health Score</span>
      <strong>98%</strong>
    </div>
    <div className="wmt-dashboard__metrics">
      <div>
        <span>Security</span>
        <strong>Secure</strong>
      </div>
      <div>
        <span>Page Speed</span>
        <strong>92</strong>
      </div>
      <div>
        <span>Uptime</span>
        <strong>99.9%</strong>
      </div>
    </div>
    <div className="wmt-dashboard__chart" aria-label="Performance chart">
      {[52, 68, 61, 78, 72, 88, 84, 96].map((h, i) => (
        <span key={i} style={{ height: `${h}%` }} />
      ))}
    </div>
    <div className="wmt-dashboard__checks">
      <div><CheckIcon /> Security audit passed</div>
      <div><CheckIcon /> Backup completed</div>
      <div><CheckIcon /> SEO updated</div>
    </div>
    <div className="wmt-float-pill">
      <span>Last Audit</span>
      <strong>Today</strong>
    </div>
  </motion.div>
);

const WebsiteMaintenancePage: React.FC = () => {
  useEffect(() => {
    document.title =
      "Website Maintenance Services & Security Audit | WebTech Bangalore";
    const description =
      "Professional website maintenance services in Bangalore — security audits, SEO, backups, and flexible monthly or annual packages.";
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, []);

  return (
    <Fragment>
      <div className="wmt-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <section className="wmt-hero" aria-labelledby="wmt-hero-title">
              <div className="wmt-hero__mesh" aria-hidden="true" />
              <div className="wmt-particles" aria-hidden="true">
                {[...Array(14)].map((_, i) => (
                  <span key={i} />
                ))}
              </div>

              <div className="wmt-container wmt-hero__grid">
                <motion.div
                  className="wmt-hero__content"
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                >
                  <span className="wmt-eyebrow">Website Maintenance Services</span>
                  <h1 id="wmt-hero-title">
                    Website Maintenance &amp; <span>Security Audit</span>
                  </h1>
                  <p>
                    Cost-effective website maintenance from one of the most trusted
                    website maintenance companies in Bangalore, India — keeping your
                    site secure, fast, and ranking high on Google.
                  </p>
                  <div className="wmt-hero__actions">
                    <Link className="wmt-btn wmt-btn--primary" to="/contact">
                      Get Maintenance Plan
                    </Link>
                    <Link className="wmt-btn wmt-btn--secondary" to="/contact">
                      Free Security Audit
                    </Link>
                  </div>
                  <div className="wmt-contact-strip">
                    <a href={`tel:${CONTACT_PHONE_TEL}`}>{CONTACT_PHONE}</a>
                    <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                  </div>
                </motion.div>

                <div className="wmt-hero__visual">
                  <MaintenanceDashboard />
                </div>
              </div>
            </section>

            <AnimatedSection className="wmt-section">
              <div className="wmt-container">
                <SectionHeader
                  eyebrow="About Our Services"
                  title="Professional Website Maintenance in Bangalore"
                />
                <div className="wmt-intro">
                  <p>
                    We are successfully providing website maintenance services. The
                    satisfactory response of our clients keeps us one among the
                    professional website maintenance companies in Bangalore, India.
                    WebTech offers cost-effective website maintenance services which
                    are available in flexible plans like prepaid, annual, and monthly
                    website maintenance packages.
                  </p>
                  <p>
                    Our website management services increase website traffic, website
                    health, and lead conversion rate. Our website maintenance packages
                    are affordable and cost-effective. We will proactively provide
                    complete website management services and save your money in the
                    long run. We have sufficient in-house web designers, web
                    developers, SEO analysts, graphic designers, content writers, and
                    website security auditors to keep your website, website backup, and
                    database up to date.
                  </p>
                  <p>
                    Every website, regardless of its complexity and size, requires
                    some form of maintenance. Whether it is adding content, images,
                    videos, graphics, and very importantly regular website health audit
                    on popular website audit portals and tools like Google PageSpeed
                    Insights, Google AMP Validation, Structured Data, and Google
                    Lighthouse Tool — the most successful sites on the internet are
                    frequently updated.
                  </p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="wmt-section wmt-section--white">
              <div className="wmt-container">
                <SectionHeader
                  title="Flexible Maintenance Plans"
                  text="Choose a plan that fits your business — prepaid, monthly, or annual website maintenance packages."
                  light
                />
                <motion.div className="wmt-plan-grid" variants={stagger}>
                  {maintenancePlans.map((plan) => (
                    <motion.article className="wmt-plan-card" variants={fadeUp} key={plan.title}>
                      <span className="wmt-plan-card__badge">{plan.badge}</span>
                      <h3>{plan.title}</h3>
                      <p>{plan.text}</p>
                      <Link className="wmt-btn wmt-btn--primary wmt-btn--sm" to="/contact">
                        Enquire Now
                      </Link>
                    </motion.article>
                  ))}
                </motion.div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="wmt-section">
              <div className="wmt-container">
                <SectionHeader title="Why Website Maintenance Matters" />
                <motion.div className="wmt-highlight-grid" variants={stagger}>
                  {highlights.map((item) => (
                    <motion.article className="wmt-glass-card" variants={fadeUp} key={item.title}>
                      <CheckIcon />
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </motion.article>
                  ))}
                </motion.div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="wmt-section wmt-section--light">
              <div className="wmt-container wmt-split">
                <div>
                  <SectionHeader
                    eyebrow="In-House Expert Team"
                    title="Dedicated specialists for every aspect of your website"
                    light
                  />
                  <ul className="wmt-team-list">
                    {inHouseTeam.map((role) => (
                      <li key={role}>
                        <CheckIcon />
                        <span>{role}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="wmt-portal-card">
                  <span className="wmt-portal-card__label">Maintenance Portal</span>
                  <h3>Track every update phase by phase</h3>
                  <p>
                    Our clients can manage and track their website development and
                    updates in our custom-designed website maintenance portal. It helps
                    track each development phase by phase with full transparency.
                  </p>
                  <div className="wmt-portal-card__steps">
                    <span>Request</span>
                    <span>In Progress</span>
                    <span>Review</span>
                    <span>Live</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="wmt-section">
              <div className="wmt-container">
                <SectionHeader
                  title="Website Health Audit Tools We Use"
                  text="Regular audits on industry-standard tools to keep your website performing at its best."
                />
                <div className="wmt-audit-grid">
                  {auditTools.map((tool) => (
                    <div className="wmt-audit-card" key={tool.name}>
                      <img src={tool.logo} alt="" loading="lazy" aria-hidden="true" />
                      <span>{tool.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="wmt-section wmt-section--white">
              <div className="wmt-container">
                <SectionHeader
                  eyebrow="Annual Website Maintenance"
                  title="Complete website management services included"
                  text="By annual website maintenance, we take care of the entire gamut of operations to keep your website secure, fast, and ranking on Google."
                  light
                />
                <div className="wmt-service-grid">
                  {annualServices.map((service) => (
                    <div className="wmt-service-item" key={service}>
                      <CheckIcon />
                      <span>{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="wmt-cta">
              <div className="wmt-container wmt-cta__inner">
                <div>
                  <span>Keep Your Website Healthy</span>
                  <h2>Ready for Proactive Website Maintenance?</h2>
                  <p>
                    Let WebTech handle your website security, updates, SEO, and
                    performance — so you can focus on growing your business.
                  </p>
                </div>
                <Link className="wmt-btn wmt-btn--primary" to="/contact">
                  Get Started Today
                </Link>
              </div>
            </AnimatedSection>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default WebsiteMaintenancePage;
